/**
 * The sync worker (docs/09 §9.4).
 *
 * Wakes on the `online` event, when the app comes to the foreground, and every
 * 30 seconds while online. Batches up to 50 pending rows and posts them to
 * /isolates/bulk-sync.
 *
 * The three principles it exists to uphold (docs/09 §9.1):
 *   1. The client owns its queue. Identity is the client UUID, so posting the
 *      same row twice writes one record.
 *   2. No silent loss. A rejected row moves to `conflict` with the reason
 *      attached and stays visible until the volunteer deals with it.
 *   3. The user knows the state at a glance, via the status this publishes.
 */

import { ApiRequestError, isolates as isolatesApi } from "./api";
import {
  countByState,
  db,
  markState,
  pendingIsolates,
  pruneSynced,
  storageStatus,
} from "./db";
import type { LocalIsolate, SyncState } from "./types";
import { backoffMs } from "./utils";

/** docs/09 §9.4: the worker batches up to 50 rows per request. */
export const BATCH_SIZE = 50;

/** docs/09 §9.4: and polls every 30 seconds while online. */
export const POLL_INTERVAL_MS = 30_000;

export interface SyncStatus {
  online: boolean;
  running: boolean;
  pending: number;
  syncing: number;
  conflicts: number;
  errors: number;
  lastSyncAt: string | null;
  lastError: string | null;
  /** docs/09 §9.9: true once the browser is nearly out of room. */
  storageNearFull: boolean;
  storageFull: boolean;
}

type Listener = (status: SyncStatus) => void;

export class SyncWorker {
  private listeners = new Set<Listener>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private started = false;
  private consecutiveFailures = 0;

  private status: SyncStatus = {
    online: typeof navigator === "undefined" ? true : navigator.onLine,
    running: false,
    pending: 0,
    syncing: 0,
    conflicts: 0,
    errors: 0,
    lastSyncAt: null,
    lastError: null,
    storageNearFull: false,
    storageFull: false,
  };

  // -- lifecycle --------------------------------------------------------
  start(): void {
    if (this.started) return;
    this.started = true;

    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
    document.addEventListener("visibilitychange", this.handleVisibility);

    this.timer = setInterval(() => {
      if (this.status.online) void this.sync();
    }, POLL_INTERVAL_MS);

    void this.refreshCounts();
    void this.sync();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;

    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
    document.removeEventListener("visibilitychange", this.handleVisibility);

    if (this.timer) clearInterval(this.timer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.timer = null;
    this.retryTimer = null;
  }

  private handleOnline = (): void => {
    this.consecutiveFailures = 0;
    this.update({ online: true });
    void this.sync();
  };

  private handleOffline = (): void => {
    this.update({ online: false });
  };

  private handleVisibility = (): void => {
    if (document.visibilityState === "visible" && this.status.online) {
      void this.sync();
    }
  };

  // -- subscriptions ----------------------------------------------------
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.status);
    return () => this.listeners.delete(listener);
  }

  getStatus(): SyncStatus {
    return this.status;
  }

  private update(patch: Partial<SyncStatus>): void {
    this.status = { ...this.status, ...patch };
    for (const listener of this.listeners) listener(this.status);
  }

  async refreshCounts(): Promise<void> {
    const counts = await countByState();
    const storage = await storageStatus();
    this.update({
      pending: counts.pending + counts.error,
      syncing: counts.syncing,
      conflicts: counts.conflict,
      errors: counts.error,
      storageNearFull: storage?.nearFull ?? false,
      storageFull: storage?.full ?? false,
    });
  }

  // -- the sync run -----------------------------------------------------
  /** Upload one batch. Safe to call at any time; it no-ops when busy. */
  async sync(): Promise<void> {
    if (this.running || !this.status.online) return;

    const batch = await pendingIsolates(BATCH_SIZE);
    if (batch.length === 0) {
      await this.refreshCounts();
      return;
    }

    this.running = true;
    this.update({ running: true, lastError: null });

    try {
      const creations = batch.filter((row) => !row.editOf);
      const edits = batch.filter((row) => row.editOf);

      if (creations.length > 0) await this.syncCreations(creations);
      for (const edit of edits) await this.syncEdit(edit);

      this.consecutiveFailures = 0;
      this.update({ lastSyncAt: new Date().toISOString() });
      await pruneSynced();
    } catch (error) {
      this.handleTransportFailure(error);
    } finally {
      this.running = false;
      this.update({ running: false });
      await this.refreshCounts();
    }
  }

  private async syncCreations(rows: LocalIsolate[]): Promise<void> {
    await Promise.all(rows.map((row) => markState(row.client_uuid, "syncing")));

    const result = await isolatesApi.bulkSync(
      rows.map((row) => ({
        client_uuid: row.client_uuid,
        payload: {
          ...row.payload,
          client_uuid: row.client_uuid,
          entered_offline: true,
          client_created_at: row.client_created_at,
        },
      })),
    );

    for (const accepted of result.synced) {
      await markState(accepted.client_uuid, "synced", {
        record_id: accepted.record_id,
        server_id: accepted.id,
        error: null,
      });
    }

    // Principle 2: a rejected row is shown, never dropped (O-07).
    for (const rejected of result.rejected) {
      await markState(rejected.client_uuid, "conflict", {
        error: rejected.message,
        errorDetail: rejected.detail,
        record_id: rejected.existing_record_id ?? null,
      });
    }

    // Anything the server did not mention is still ours to retry.
    const answered = new Set([
      ...result.synced.map((r) => r.client_uuid),
      ...result.rejected.map((r) => r.client_uuid),
    ]);
    for (const row of rows) {
      if (!answered.has(row.client_uuid)) {
        await markState(row.client_uuid, "pending", { attempts: row.attempts + 1 });
      }
    }
  }

  /** docs/09 §9.7: an offline edit, with optimistic concurrency. */
  private async syncEdit(row: LocalIsolate): Promise<void> {
    if (!row.editOf) return;
    await markState(row.client_uuid, "syncing");

    try {
      await isolatesApi.update(row.editOf, {
        ...row.payload,
        expected_updated_at: row.expectedUpdatedAt ?? null,
      });
      await markState(row.client_uuid, "synced", { error: null });
    } catch (error) {
      if (error instanceof ApiRequestError) {
        if (error.isOffline) {
          await markState(row.client_uuid, "pending", { attempts: row.attempts + 1 });
          throw error;
        }
        // 409 means the row moved under us: the volunteer decides (docs/09 §9.7).
        await markState(row.client_uuid, "conflict", {
          error: error.message,
          errorDetail: error.detail,
        });
        return;
      }
      throw error;
    }
  }

  /**
   * Network or 5xx: back off and try again (1s, 2s, 4s ... capped at 5
   * minutes). The rows stay `pending`, so nothing is lost.
   */
  private handleTransportFailure(error: unknown): void {
    this.consecutiveFailures += 1;

    const message =
      error instanceof ApiRequestError
        ? error.isOffline
          ? "Could not reach the server."
          : error.message
        : "Sync failed.";
    this.update({ lastError: message });

    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      void this.sync();
    }, backoffMs(this.consecutiveFailures));
  }

  /** Re-queue a conflicted row after the volunteer has corrected it. */
  async retry(clientUuid: string): Promise<void> {
    await markState(clientUuid, "pending", { error: null, errorDetail: null });
    await this.refreshCounts();
    void this.sync();
  }

  /** Discard a conflicted row the volunteer has decided against. */
  async discard(clientUuid: string): Promise<void> {
    await db.isolates_local.delete(clientUuid);
    await this.refreshCounts();
  }
}

export const syncWorker = new SyncWorker();

/** The header badge text (docs/09 §9.1, principle 3). */
export function describeStatus(status: SyncStatus): {
  label: string;
  tone: "ok" | "pending" | "busy" | "error";
} {
  if (status.conflicts > 0 || status.errors > 0) {
    return { label: "Sync problem — tap to review", tone: "error" };
  }
  if (status.running && status.pending > 0) {
    return { label: `Syncing, ${status.pending} remaining`, tone: "busy" };
  }
  if (!status.online) {
    return {
      label: status.pending > 0 ? `Offline, ${status.pending} queued` : "Offline",
      tone: "pending",
    };
  }
  if (status.pending > 0) {
    return { label: `${status.pending} waiting to sync`, tone: "pending" };
  }
  return { label: "Online, all synced", tone: "ok" };
}

/** Map a sync state to the row icon used in lists (docs/09 §9.3). */
export function stateIcon(state: SyncState): "clock" | "spinner" | "check" | "alert" {
  switch (state) {
    case "pending":
      return "clock";
    case "syncing":
      return "spinner";
    case "synced":
      return "check";
    case "conflict":
    case "error":
      return "alert";
    default:
      return "clock";
  }
}
